/**
 * Focus operations — "In Focus" feature.
 *
 * Tasks marked as focused appear in a persistent banner across all views.
 * Each focused task is stored as an individual setting with prefix `#focus:`.
 * The task itself is NOT modified — focus is a pure UI-layer.
 */

import { getDb, getNow } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { ObjectStatus } from "./db-types.js"
import type { DbObject } from "./db-types.js"

const FOCUS_PREFIX = "#focus:"
const FOCUS_MAX_SETTING = "#focus-max-tasks"
const DEFAULT_MAX = 3

/**
 * Add a task to In Focus.
 * Stores a `#focus:{taskId}` setting with value = focusedAt timestamp.
 */
export async function focusTask(taskId: string): Promise<OpResult> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task) return fail("Task not found")

  // Recurring templates cannot be focused — only instances can
  if (task.isRecurringTemplate === 1) {
    return fail("Cannot focus a recurring template — focus individual instances instead")
  }

  // Already focused?
  const existing = await db.setting.get(`${FOCUS_PREFIX}${taskId}`)
  if (existing) return ok()

  // Max check
  const maxSetting = await db.setting.get(FOCUS_MAX_SETTING)
  const max = maxSetting ? Number(maxSetting.value) : DEFAULT_MAX
  const current = await db.setting
    .where("id")
    .startsWith(FOCUS_PREFIX)
    .count()
  if (current >= max) return fail(`Maximum ${max} focused tasks reached`)

  const now = getNow()
  await db.setting.put({
    id: `${FOCUS_PREFIX}${taskId}`,
    value: now,
    device: "",
    createdAt: now,
    createdBy: "",
  })

  return ok()
}

/**
 * Remove a task from In Focus.
 */
export async function unfocusTask(taskId: string): Promise<OpResult> {
  const db = getDb()
  await db.setting.delete(`${FOCUS_PREFIX}${taskId}`)
  return ok()
}

/**
 * Check if a specific task is focused.
 */
export async function isTaskFocused(taskId: string): Promise<boolean> {
  const db = getDb()
  return !!(await db.setting.get(`${FOCUS_PREFIX}${taskId}`))
}

/**
 * Get all focused tasks, FIFO-sorted (oldest first).
 * Returns stale entry IDs for cleanup by the caller (outside liveQuery).
 */
export async function getFocusedTasks(): Promise<DbObject[]> {
  const db = getDb()
  const entries = await db.setting
    .where("id")
    .startsWith(FOCUS_PREFIX)
    .toArray()

  if (entries.length === 0) return []

  // Sort FIFO by value (= focusedAt timestamp)
  // Clone before sorting — Dexie may return a read-only array
  const sorted = [...entries].sort((a, b) => Number(a.value) - Number(b.value))

  // Fetch actual tasks
  const taskIds = sorted.map((e) => e.id!.slice(FOCUS_PREFIX.length))
  const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))

  // Separate valid from stale
  const valid: DbObject[] = []
  const toDelete: string[] = []

  for (let i = 0; i < tasks.length; i++) {
    const t = tasks[i]
    const entry = sorted[i]
    if (t && t.status === ObjectStatus.Unfinished && !t.isDeleted && t.isRecurringTemplate !== 1) {
      valid.push(t)
    } else if (entry?.id) {
      toDelete.push(entry.id)
    }
  }

  // Store stale IDs for deferred cleanup (must not write inside liveQuery)
  if (toDelete.length > 0) {
    _pendingFocusCleanup = toDelete
  }

  return valid
}

/** Stale focus entries awaiting cleanup (written by getFocusedTasks, consumed by cleanupStaleFocusEntries). */
let _pendingFocusCleanup: string[] = []

/** Call OUTSIDE liveQuery to delete stale focus entries. Safe to call any time. */
export async function cleanupStaleFocusEntries(): Promise<void> {
  const ids = _pendingFocusCleanup
  if (ids.length === 0) return
  _pendingFocusCleanup = []
  const db = getDb()
  await db.setting.bulkDelete(ids).catch(() => {
    // Best-effort — ignore failures
  })
}

/**
 * Get the count of currently focused tasks (without loading task data).
 */
export async function getFocusedCount(): Promise<number> {
  const db = getDb()
  return db.setting.where("id").startsWith(FOCUS_PREFIX).count()
}
